import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook to fetch and manage audit history for an entity.
 *
 * @param {string} entityType - Type of entity (hazard, risk_assessment, permit, etc.)
 * @param {string} entityId - ID of the entity
 * @param {Object} options - Optional configuration
 * @param {number} options.limit - Page size (default: 20)
 * @param {boolean} options.autoFetch - Auto-fetch on mount (default: true)
 * @returns {Object} Audit history state and functions
 */
export function useAuditHistory(entityType, entityId, options = {}) {
  const { limit = 20, autoFetch = true, includeVersions = false } = options;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalEntries, setTotalEntries] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);
  // Referenced record id -> display name, so the viewer can show
  // "Engineering" where the row stores a department UUID. Resolved by the API
  // inside this tenant; ids it could not resolve are simply absent.
  const [referenceNames, setReferenceNames] = useState({});

  const fetchHistory = useCallback(async (skip = 0, append = false) => {
    if (!entityType || !entityId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const params = { limit, skip };
      if (includeVersions) {
        params.include_versions = true;
      }
      const response = await axios.get(
        `${API_BASE}/api/v1/audit/${entityType}/${entityId}/history`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = response.data;

      if (append) {
        setHistory(prev => [...prev, ...data.entries]);
        // A later page resolves its own ids; keep the names already known.
        setReferenceNames(prev => ({ ...prev, ...(data.reference_names || {}) }));
      } else {
        setHistory(data.entries);
        setReferenceNames(data.reference_names || {});
      }

      setTotalEntries(data.total_entries);
      setHasMore(data.has_more);
      setCurrentSkip(skip);
    } catch (err) {
      console.error('Failed to fetch audit history:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch audit history');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, limit]);

  // Load more entries
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const newSkip = currentSkip + limit;
      fetchHistory(newSkip, true);
    }
  }, [loading, hasMore, currentSkip, limit, fetchHistory]);

  // Refresh from beginning
  const refresh = useCallback(() => {
    setHistory([]);
    setReferenceNames({});
    setCurrentSkip(0);
    fetchHistory(0, false);
  }, [fetchHistory]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && entityType && entityId) {
      fetchHistory(0, false);
    }
  }, [autoFetch, entityType, entityId, fetchHistory]);

  return {
    history,
    loading,
    error,
    totalEntries,
    hasMore,
    loadMore,
    refresh,
    fetchHistory,
    referenceNames
  };
}

/**
 * Custom hook to fetch a document snapshot at a specific audit version.
 *
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @returns {Object} Snapshot state and fetch function
 */
export function useAuditSnapshot(entityType, entityId) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSnapshot = useCallback(async (auditId) => {
    if (!entityType || !entityId || !auditId) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE}/api/v1/audit/${entityType}/${entityId}/snapshot/${auditId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSnapshot(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch snapshot:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch snapshot');
      return null;
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  const clearSnapshot = useCallback(() => {
    setSnapshot(null);
    setError(null);
  }, []);

  return {
    snapshot,
    loading,
    error,
    fetchSnapshot,
    clearSnapshot
  };
}

export default useAuditHistory;
